import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ITableState } from './types';
import { TrendType } from '../chart/types';
import { normalizeCreatedMetrics, normalizeCustomGroups } from '../../../../../../helpers/utils/functions';
import { prepareRows } from '../../../../../../helpers/utils/reports';
import { listKeysGroupings, listKeysMetrics } from '../../../../../../shared/common/components/reports/modal-settings/index';

export const defaultAdsGroupings = [
  { dataKey: 'ads_source',  key: 'ads_source',  name: 'Источник рекламы', label: 'Источник рекламы' },
  { dataKey: 'ads_campaign', key: 'ads_campaign', name: 'Название кампаний', label: 'Название кампаний' },
  { dataKey: 'ads_adv_id',  key: 'ads_adv_id',  name: 'ID объявления', label: 'ID объявления' },
];
export const defaultGroupings = [ // Дефолтные группировки
    'reg_utm_source',
    'reg_utm_medium',
    'reg_utm_campaign',
].map((dataKey) => listKeysGroupings.find((item: any) => item.dataKey === dataKey));
export const defaultMetrics = [ // Дефолтные метрики
    'regs_count',
    'users_count',
    'orders_count',
    'orders_sum',
    'payments_count',
    'payments_sum',
    'orders_to_forders_converse',
    'user_to_buyer_converse',
].map((dataKey) =>
    listKeysMetrics.find((item: any) => item.dataKey === dataKey));

const defaultMetricsRegNotPrepareNumber = [
    'regs_count',
].map((dataKey) =>
    listKeysMetrics.find((item: any) => item.dataKey === dataKey)
);

export const defaultMetricsReg = defaultMetricsRegNotPrepareNumber
    .filter((item): item is NonNullable<typeof item> => !!item)
    .map((item) => ({
        ...item,
        dataKey: item.dataKey,
    }));


const defaultGroupingsRegNotPrepareNumber = [
    'reg_date_y',
].map((dataKey) =>
    listKeysGroupings.find((item: any) => item.dataKey === dataKey)
);

export const defaultGroupingReg = defaultGroupingsRegNotPrepareNumber
    .filter((item): item is NonNullable<typeof item> => !!item)
    .map((item) => ({
        ...item,
        dataKey: item.dataKey,
    }));

const fakeCreatedMetricsData =  [ {
    name: 'Созданные метрики',
    children: []
} ];

// Корневой узел «Группы регистраций» — динамические группировки. children заполняются из API.
const fakeCustomGroupsData = [ {
    name: 'Группы регистраций',
    children: []
} ];

const initialState: ITableState = {
    groupings: (defaultGroupings ?? []).filter(Boolean),
    metrics: (defaultMetrics ?? []).filter(Boolean),
    createdMetrics:fakeCreatedMetricsData || [],
    customGroups: fakeCustomGroupsData,
    // Справочник секций разобранных UTM-меток (рекламные системы и их параметры)
    utmSections: [],
    groupsChecked: [],
    metricsChecked: [],
    totalChecked: [],
    percentages: [],
    // Колонки (dataKey), где включён спарклайн — мини-график по периодам в ячейках
    sparklines: [] as string[],
    // Заливка ячеек обычных метрик по условиям «от – до» (dataKey → настройки, см. CellFillSettings в tableHelpers)
    cellFills: {} as Record<string, { isFilling: boolean; fillingConditions: Array<{ from: number | string; to: number | string; value: string }> }>,
    // Блоки дашборда: «Динамика» и заливка всей таблицы (кнопки над таблицей) сохраняются в блоке.
    // В отчётах эти кнопки по-прежнему живут только в ReportTable и сюда не пишутся
    analysis: false,
    cellFilling: false,
    tableData: {
        data: [],
        total: {},
        totalChosen:{
            active:[],
            metrics:{}
        }
    },
    filtersTable: {},
    sortTable: {},
    groupsLimit: false,
    metricsLimit: false,
    metrics_filters_update: false,

    tableReportData: {
        data: [],
        total: {}
    },
    metricsReport: [],
    metricsReportChecked: [],
    blocks: {},
    // Режим выбора группировок: 'manual' (ручной), 'all' (выбрать все), 'firstN' (первые N строк)
    groupsSelectionMode: 'manual' as 'manual' | 'all' | 'firstN',
    // Количество первых строк для режима 'firstN'
    selectFirstNRows: null as number | null,
    // Закреплённые колонки
    frozensColumns: {} as Record<string, boolean>,
};
const initialBlockState = {
    // ВАЖНО: Не используем defaultGroupings и defaultMetrics для блоков дашборда,
    // т.к. они содержат regs_count и другие метрики, которые могут отсутствовать
    // в конкретном отчёте. Метрики и группировки должны приходить из сохранённых данных блока.
    groupings: [],
    metrics: [],
    createdMetrics:fakeCreatedMetricsData || [],
    groupsChecked: [],
    metricsChecked: [],
    totalChecked: [],
    percentages: [],
    // Колонки (dataKey), где включён спарклайн — мини-график по периодам в ячейках
    sparklines: [] as string[],
    // Заливка ячеек обычных метрик по условиям «от – до» (dataKey → настройки, см. CellFillSettings в tableHelpers)
    cellFills: {} as Record<string, { isFilling: boolean; fillingConditions: Array<{ from: number | string; to: number | string; value: string }> }>,
    // «Динамика» и заливка всей таблицы, включённые в окне блока
    analysis: false,
    cellFilling: false,
    tableData: {
        data: [],
        total: {},
        totalChosen:{
            active:[],
            metrics:{}
        }
    },
    filtersTable: {},
    sortTable: {},
    groupsLimit: false,
    metricsLimit: false,
    metrics_filters_update: false,

    tableReportData: {
        data: [],
        total: {}
    },
    metricsReport: [],
    metricsReportChecked: [],
    groupsSelectionMode: 'manual' as 'manual' | 'all' | 'firstN',
    selectFirstNRows: null as number | null,
    // Закреплённые колонки
    frozensColumns: {} as Record<string, boolean>,
};

export const tableSlice = createSlice({
    name: 'table',
    initialState: initialState,
    reducers: {
        setDashboardsBlocksTableInitialState:(state,action: PayloadAction<{ids:string[],data?:Partial<Omit<ITableState,'blocks'>>}>) => {
            const { ids,data } = action.payload;

            const newBlocksValues:{[key:string]:Omit<ITableState,'blocks'>} = {};
            ids.forEach((el)=>{
                newBlocksValues[el] = state.blocks[ids[0]] || initialBlockState;
            });
            if(data){
                newBlocksValues[ids[0]] = { ...newBlocksValues[ids[0]],...data };
            }
            state.blocks = { ...state.blocks,...newBlocksValues };
        },
        setCreatedMetrics:(state,action:PayloadAction<any>) => {
            const newCreatedMetrics = normalizeCreatedMetrics(action.payload);
            
            // Функция для обновления массива метрик
            // Возвращает null если ничего не изменилось (для избежания лишних перерисовок)
            const updateMetricsArray = (metricsArray: any[]) => {
                let hasChanges = false;
                const updated = metricsArray.map(metric => {
                    const correspondingMetric = newCreatedMetrics.find(nm => nm.dataKey === metric.dataKey);
                    if (correspondingMetric) {
                        hasChanges = true;
                        return { ...metric, ...correspondingMetric };
                    }
                    return metric;
                });
                return hasChanges ? updated : null;
            };
            
            // Функция для обновления массива metricsChecked
            const updateMetricsCheckedArray = (metricsCheckedArray: any[]) => {
                let hasChanges = false;
                const updated = metricsCheckedArray.map(metric => {
                    const correspondingMetric = newCreatedMetrics.find(nm => nm.dataKey === metric.dataKey);
                    if (correspondingMetric) {
                        hasChanges = true;
                        return {
                            ...metric,
                            label: correspondingMetric.name,
                            plan: correspondingMetric.plan ?? metric.plan,
                            base_metric_name: correspondingMetric.base_metric_name,
                            metric_formula: correspondingMetric.metric_formula,
                            background: correspondingMetric.background,
                            fillingConditions: correspondingMetric.fillingConditions,
                            isFilling: correspondingMetric.isFilling,
                            unit: correspondingMetric.unit,
                        };
                    }
                    return metric;
                });
                return hasChanges ? updated : null;
            };
            
            // Обновляем основные метрики только если есть изменения (избегаем лишних перерисовок)
            const updatedMetrics = updateMetricsArray(state.metrics);
            if (updatedMetrics) {
                state.metrics = updatedMetrics;
            }
            
            const updatedMetricsChecked = updateMetricsCheckedArray(state.metricsChecked);
            if (updatedMetricsChecked) {
                state.metricsChecked = updatedMetricsChecked;
            }
            
            // Обновляем метрики во всех блоках дашбордов только если есть изменения
            Object.keys(state.blocks).forEach(blockId => {
                if (state.blocks[blockId]) {
                    const updatedBlockMetrics = updateMetricsArray(state.blocks[blockId].metrics);
                    if (updatedBlockMetrics) {
                        state.blocks[blockId].metrics = updatedBlockMetrics;
                    }
                    
                    const updatedBlockMetricsChecked = updateMetricsCheckedArray(state.blocks[blockId].metricsChecked);
                    if (updatedBlockMetricsChecked) {
                        state.blocks[blockId].metricsChecked = updatedBlockMetricsChecked;
                    }
                }
            });
            
            state.createdMetrics[0].children = newCreatedMetrics;
        },
        // Справочник секций разобранных UTM-меток. Массив секций из ответа
        // GET /utm-params/sections; при смене школы просто перезаписывается.
        setUtmSections: (state, action: PayloadAction<any>) => {
            state.utmSections = Array.isArray(action.payload) ? action.payload : [];
        },
        setCustomGroups: (state, action: PayloadAction<any>) => {
            const newCustomGroups = normalizeCustomGroups(action.payload);

            // ВАЖНО: только ОБНОВЛЯЕМ имена выбранных групп по dataKey (напр. после переименования).
            // НЕ удаляем группы из выбранных, если их нет в свежем списке — список грузится
            // асинхронно, и при загрузке страницы это выкидывало бы восстановленные custom_group_*.
            // Удаление из выбранных делается явно при удалении группы (confirmGroupRemoving).
            const updateGroupingsArray = (groupingsArray: any[]) => {
                let hasChanges = false;
                const updated = groupingsArray.map((grouping) => {
                    const corresponding = newCustomGroups.find((ng) => ng.dataKey === grouping?.dataKey);
                    if (corresponding) {
                        hasChanges = true;
                        return { ...grouping, ...corresponding, label: corresponding.name };
                    }
                    return grouping;
                });
                return hasChanges ? updated : null;
            };

            const updatedGroupings = updateGroupingsArray(state.groupings);
            if (updatedGroupings) {
                state.groupings = updatedGroupings;
            }

            // Аналогично для блоков дашбордов
            Object.keys(state.blocks).forEach((blockId) => {
                if (state.blocks[blockId]) {
                    const updatedBlockGroupings = updateGroupingsArray(state.blocks[blockId].groupings);
                    if (updatedBlockGroupings) {
                        state.blocks[blockId].groupings = updatedBlockGroupings;
                    }
                }
            });

            state.customGroups[0].children = newCustomGroups;
        },
        setTotalChosenStatus: (state,action:PayloadAction<any>) => {
            state.tableData.totalChosen.active = action.payload;
        },
        setTotalChosenMetrics: (state, action: PayloadAction<any>) => {
            state.tableData.totalChosen.metrics = { ...action.payload };
        },
        setMetrics: (state, action: PayloadAction<any>) => {
            const { blockId,data } = action.payload;

            if(blockId){
                state.blocks[blockId].metrics = data;
            }else{
                state.metrics = data;
            }
        },
        setMetricsChecked: (state, action: PayloadAction<any>) => {
            const { blockId,data } = action.payload;

            if(blockId){
                state.blocks[blockId].metricsChecked = data;
            }else{
                state.metricsChecked = data;
            }
        },
        setGroupings: (state, action: PayloadAction<any>) => {
            const { blockId,data } = action.payload;
            if(blockId){
                state.blocks[blockId].groupsChecked = [];
                state.blocks[blockId].groupings = data ?? [];
            }else{
                state.groupsChecked = [];
                state.groupings = data ?? [];
            }
        },
        setGroupingsChecked: (state, action: PayloadAction<any | { data:any[],blockId:string }>) => {
            if(Array.isArray(action.payload)){
                const getGroupsCheckedObject = () => {
                    const url = new URL(window.location.href);
                    const currentParams = url.searchParams.get('params');
                    const paramsObject = currentParams ? JSON.parse(currentParams) : {};
                    if(paramsObject?.groupsChecked?.includes(action.payload[action.payload.length-1].pathStr)){
                        return paramsObject?.groupsChecked;
                    }else{
                        return action.payload.map((el:{pathStr:string})=>el.pathStr);
                    }
                };
                state.groupsChecked = action.payload;
            }else {
                if((action.payload as unknown as  {data:any[],blockId:string})?.blockId) {
                    state.blocks[action.payload.blockId].groupsChecked = action.payload.data || [];
                }
            }
        },
        setTotalChecked: (state) => {
            state.totalChecked = [ { label: 'Итого и среднее', pathStr: 'total' } ];
        },
        clearTotalChecked: (state) => {
            state.totalChecked = [];
        },
        setPercentages: (state, action: PayloadAction<any>) => {
            if(Array.isArray(action.payload)){
                state.percentages = action.payload;
            }else{
                state.blocks[action.payload.blockId].percentages = action.payload.data;
            }
        },
        // Спарклайны по колонкам — как setPercentages: массив глобально или { blockId, data } для блока
        setSparklines: (state, action: PayloadAction<string[] | { blockId: string; data: string[] }>) => {
            if (Array.isArray(action.payload)) {
                state.sparklines = action.payload;
            } else if (state.blocks[action.payload.blockId]) {
                state.blocks[action.payload.blockId].sparklines = action.payload.data;
            }
        },
        // Заливка ячеек по колонкам: объект dataKey → настройки глобально или { blockId, data } для блока
        setCellFills: (state, action: PayloadAction<Record<string, any> | { blockId: string; data: Record<string, any> }>) => {
            const payload = action.payload as { blockId?: unknown; data?: Record<string, any> };
            if (typeof payload.blockId === 'string' && payload.data && typeof payload.data === 'object') {
                if (state.blocks[payload.blockId]) {
                    state.blocks[payload.blockId].cellFills = payload.data;
                }
            } else {
                state.cellFills = action.payload as Record<string, any>;
            }
        },
        // «Динамика» блока дашборда: boolean для окна блока или { blockId, data } для самого блока
        setTableAnalysis: (state, action: PayloadAction<boolean | { blockId: string; data: boolean }>) => {
            if (typeof action.payload === 'boolean') {
                state.analysis = action.payload;
            } else if (state.blocks[action.payload.blockId]) {
                state.blocks[action.payload.blockId].analysis = action.payload.data;
            }
        },
        // Заливка всей таблицы в блоке дашборда — так же, как setTableAnalysis
        setTableCellFilling: (state, action: PayloadAction<boolean | { blockId: string; data: boolean }>) => {
            if (typeof action.payload === 'boolean') {
                state.cellFilling = action.payload;
            } else if (state.blocks[action.payload.blockId]) {
                state.blocks[action.payload.blockId].cellFilling = action.payload.data;
            }
        },
        setFiltersTable: (state, action: PayloadAction<any>) => {
            if (action.payload && typeof action.payload === 'object') {
                if (!Object.hasOwnProperty.call(action.payload, 'blockId')) {
                    state.filtersTable = action.payload;
                } else {
                    state.blocks[action.payload.blockId].filtersTable = action.payload.data;
                }
            } else {
                console.error('Ошибка: action.payload не является объектом или равен null/undefined');
            }
        },
        setSortTable: (state, action: PayloadAction<any>) => {
            if(Object.hasOwn(action.payload ?? {},'blockId') && action.payload?.blockId){
                state.blocks[action.payload.blockId].sortTable =  action.payload.data;
            }else{
                state.sortTable = Object.hasOwn(action.payload ?? {},'blockId') ? action.payload.data : action.payload;
            }
        },
        setFrozensColumns: (state, action: PayloadAction<Record<string, boolean>>) => {
            state.frozensColumns = action.payload;
        },
        setGroupsLimit: (state, action: PayloadAction<boolean>) => {
            state.groupsLimit = action.payload;
        },
        setMetricsLimit: (state, action: PayloadAction<boolean>) => {
            state.metricsLimit = action.payload;
        },
        setMetricsFiltersUpdate: (state, action: PayloadAction<boolean>) => {
            state.metrics_filters_update = action.payload;
        },
        setTableData: (state, action: PayloadAction<any>) => {
            const { blockId, flatMode, groupFilter } = action.payload;
            // Место для строк предпросмотра окна блока (dataSlotId) создаётся при первой записи
            // Полная копия: вложенные объекты initialBlockState заморожены immer (их уже клали в state),
            // и запись tableData.data в мелкую копию падала бы
            if (blockId && !state.blocks[blockId]) {
                state.blocks[blockId] = JSON.parse(JSON.stringify(initialBlockState));
            }
            if(blockId){
                state.blocks[blockId].tableData.data = prepareRows({ rows: action.payload.rows, groupings: action.payload.groupings, flatMode, groupFilter });
                state.blocks[blockId].tableData.total = action.payload.total;
            }else{
                state.tableData.data = prepareRows({ rows: action.payload.rows, groupings: action.payload.groupings, flatMode, groupFilter });
                state.tableData.total = action.payload.total;
            }

        },
        setTableChildrenData: (state, action: PayloadAction<any>) => {
            const { loadCurrent, data, groupings, blockId, groupFilter } = action.payload;
            const recurseChildren = (rows: any) => {
                for (let index = 0; index < rows.length; index++) {
                    const row = rows[index];
                    if (row.children && row.children.length > 0) {
                        recurseChildren(row.children);
                    }
                    if (row.pathStr === loadCurrent.pathStr) {
                        row.children = prepareRows({ rows: data.rows, parent: loadCurrent, groupings, groupFilter });
                        row.clientData.downloaded = true;
                        row.clientData.loading = false;
                    }
                }
                return rows;
            };
            if(blockId && state.blocks[blockId]){
                state.blocks[blockId].tableData.data = recurseChildren(state.blocks[blockId].tableData.data);
            }else{
                state.tableData.data = recurseChildren(state.tableData.data);
            }
        },

        setReportTableData: (state, action: PayloadAction<any>) => {
            state.tableReportData.data = prepareRows({ rows: action.payload.rows, groupings: action.payload.groupings, groupFilter: action.payload.groupFilter });
            state.tableReportData.total = action.payload.total;
        },
        updateReportTableRow: (state, action: PayloadAction<{ rowId: number | string, field: string, value: any }>) => {
            const { rowId, field, value } = action.payload;
            const row = state.tableReportData.data.find((r: any) => r.id === rowId);
            if (row) {
                row[field] = value;
            }
        },
        setReportClearTableData: (state) => {
            state.tableReportData.data = [];
            state.tableReportData.total = {};
        },
        setReportMetrics: (state, action: PayloadAction<any>) => {
            state.metricsReport = action.payload;
        },
        setReportMetricsChecked: (state, action: PayloadAction<any>) => {
            state.metricsReportChecked = action.payload;
        },
        setTableClearState: (state) => {
            // Сбрасываем все данные кроме blocks и createdMetrics
            // createdMetrics не сбрасываем — они загружаются отдельно через getCreatedMetrics
            // и не зависят от конкретного отчёта
            state.groupings = [];
            state.metrics = [];
            state.groupsChecked = [];
            state.metricsChecked = [];
            state.totalChecked = [];
            state.percentages = [];
            state.sparklines = [];
            state.cellFills = {};
            state.analysis = false;
            state.cellFilling = false;
            state.tableData = {
                data: [],
                total: {},
                totalChosen: {
                    active: [],
                    metrics: {}
                }
            };
            state.filtersTable = {};
            state.sortTable = {};
            state.groupsLimit = false;
            state.metricsLimit = false;
            state.metrics_filters_update = false;
            state.tableReportData = {
                data: [],
                total: {}
            };
            state.metricsReport = [];
            state.metricsReportChecked = [];
            state.groupsSelectionMode = 'manual';
            state.selectFirstNRows = null;
        },
        setGroupsSelectionMode: (state, action: PayloadAction<'manual' | 'all' | 'firstN' | { data: 'manual' | 'all' | 'firstN'; blockId: string }>) => {
            if (typeof action.payload === 'object' && action.payload?.blockId) {
                if (state.blocks[action.payload.blockId]) {
                    state.blocks[action.payload.blockId].groupsSelectionMode = action.payload.data;
                }
            } else {
                state.groupsSelectionMode = action.payload as 'manual' | 'all' | 'firstN';
            }
        },
        setSelectFirstNRows: (state, action: PayloadAction<number | null | { data: number | null; blockId: string }>) => {
            if (typeof action.payload === 'object' && action.payload !== null && 'blockId' in action.payload) {
                if (state.blocks[action.payload.blockId]) {
                    state.blocks[action.payload.blockId].selectFirstNRows = action.payload.data;
                }
            } else {
                state.selectFirstNRows = action.payload as number | null;
            }
        },
        clearBlockTableState: (state, action: PayloadAction<string>) => {
            const blockId = action.payload;
            if (state.blocks[blockId]) {
                delete state.blocks[blockId];
            }
        },
        /**
         * Загружает данные блока дашборда из сохранённого состояния.
         * Используется при инициализации дашборда для восстановления
         * metricsChecked, groupsChecked, percentages и других настроек.
         */
        loadBlockTableData: (state, action: PayloadAction<{
            blockId: string;
            data: Partial<Omit<ITableState, 'blocks'>>;
        }>) => {
            const { blockId, data } = action.payload;
            if (!state.blocks[blockId]) {
                state.blocks[blockId] = { ...initialBlockState };
            }
            // Мержим сохранённые данные с текущим состоянием блока
            state.blocks[blockId] = {
                ...state.blocks[blockId],
                ...data,
            };
        },
        /**
         * Обновляет настройки отображения для конкретной метрики в metricsChecked.
         * Используется для управления showTrend, hideLine и trendType.
         */
        updateMetricDisplaySettings: (state, action: PayloadAction<{
            dataKey: string;
            settings: { showTrend?: boolean; hideLine?: boolean; trendType?: TrendType };
            blockId?: string;
        }>) => {
            const { dataKey, settings, blockId } = action.payload;
            
            const updateMetric = (metricsArray: any[]) => {
                return metricsArray.map(metric => {
                    // Проверяем по dataKey или name (для совместимости)
                    if (metric.dataKey === dataKey || metric.name === dataKey) {
                        return { ...metric, ...settings };
                    }
                    return metric;
                });
            };
            
            if (blockId && state.blocks[blockId]) {
                state.blocks[blockId].metricsChecked = updateMetric(state.blocks[blockId].metricsChecked);
            } else {
                state.metricsChecked = updateMetric(state.metricsChecked);
            }
        },
        /**
         * Переключает showTrend для конкретной метрики.
         */
        toggleMetricTrend: (state, action: PayloadAction<{
            dataKey: string;
            blockId?: string;
        }>) => {
            const { dataKey, blockId } = action.payload;
            
            const toggleTrend = (metricsArray: any[]) => {
                return metricsArray.map(metric => {
                    if (metric.dataKey === dataKey || metric.name === dataKey) {
                        if (metric.showTrend === false) {
                            // Явно выключен → удаляем per-metric настройки, вернётся к глобальным
                            const { showTrend, trendType, ...rest } = metric;
                            return rest;
                        }
                        // undefined (следует глобальному) или true (явно включён) → явно выключаем
                        return { ...metric, showTrend: false };
                    }
                    return metric;
                });
            };
            
            if (blockId && state.blocks[blockId]) {
                state.blocks[blockId].metricsChecked = toggleTrend(state.blocks[blockId].metricsChecked);
            } else {
                state.metricsChecked = toggleTrend(state.metricsChecked);
            }
        },
        /**
         * Сбрасывает per-metric showTrend у всех метрик (удаляет showTrend/trendType).
         * Метрики возвращаются к следованию глобальному переключателю.
         */
        resetMetricsTrendOverride: (state, action: PayloadAction<{ blockId?: string } | undefined>) => {
            const blockId = action.payload?.blockId;

            const resetTrends = (metricsArray: any[]) => {
                return metricsArray.map(metric => {
                    if ('showTrend' in metric || 'trendType' in metric) {
                        const { showTrend, trendType, ...rest } = metric;
                        return rest;
                    }
                    return metric;
                });
            };

            if (blockId && state.blocks[blockId]) {
                state.blocks[blockId].metricsChecked = resetTrends(state.blocks[blockId].metricsChecked);
            } else {
                state.metricsChecked = resetTrends(state.metricsChecked);
            }
        },
        /**
         * Переключает hideLine для конкретной метрики (скрыть/показать основной график).
         */
        toggleMetricLine: (state, action: PayloadAction<{
            dataKey: string;
            blockId?: string;
        }>) => {
            const { dataKey, blockId } = action.payload;
            
            const toggleLine = (metricsArray: any[]) => {
                return metricsArray.map(metric => {
                    if (metric.dataKey === dataKey || metric.name === dataKey) {
                        // По умолчанию hideLine = false (график показан)
                        const currentHideLine = metric.hideLine === true;
                        return { ...metric, hideLine: !currentHideLine };
                    }
                    return metric;
                });
            };
            
            if (blockId && state.blocks[blockId]) {
                state.blocks[blockId].metricsChecked = toggleLine(state.blocks[blockId].metricsChecked);
            } else {
                state.metricsChecked = toggleLine(state.metricsChecked);
            }
        },
        /**
         * Устанавливает тип тренда для конкретной метрики.
         * Если trendType не передан — сбрасывает на undefined (наследует глобальный defaultTrendType).
         */
        setMetricTrendType: (state, action: PayloadAction<{
            dataKey: string;
            trendType?: TrendType;
            blockId?: string;
        }>) => {
            const { dataKey, trendType, blockId } = action.payload;

            const updateTrendType = (metricsArray: any[]) => {
                return metricsArray.map(metric => {
                    if (metric.dataKey === dataKey || metric.name === dataKey) {
                        if (trendType) {
                            return { ...metric, trendType };
                        }
                        // Сброс — удаляем trendType, метрика наследует глобальный
                        const { trendType: _, ...rest } = metric;
                        return rest;
                    }
                    return metric;
                });
            };

            if (blockId && state.blocks[blockId]) {
                state.blocks[blockId].metricsChecked = updateTrendType(state.blocks[blockId].metricsChecked);
            } else {
                state.metricsChecked = updateTrendType(state.metricsChecked);
            }
        },
    }
});

export const tableSliceActions = tableSlice.actions;
export const tableSliceReducer = tableSlice.reducer;
